import {
  Box,
  Breadcrumbs,
  CardContent,
  Card,
  Divider,
  Typography,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material'
import { useMemo, useState } from 'react'
import FilterMonthAttendance from '@/components/attendance/detail-attendance/FillterMonthAttendance'
import { useRouteQuery, useTranslator } from '@/hooks'
import { StatusLogsEnum, IUser, Activity } from '@/@data'
import Link from '@/components/Link'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import CustomAvatar from '@/@core/components/mui/Avatar'
import { getInitials } from '@/utils/getInitials'
import { TablePagination } from '@/@core/components/mui/TablePagination'

export const LiftDetailActivityLog = () => {
  const { __ } = useTranslator()
  const { searchQuery, updateQuery } = useRouteQuery()
  const [selectedTypes, setSelectedTypes] = useState<string[]>(Object.values(StatusLogsEnum))
  const navigate = useNavigate()

  const attendanceTypeOptions = [
    {
      label: __('Error'),
      value: StatusLogsEnum.Error,
      color: 'rgba(255, 76, 81, 1)',
      backgroundColor: 'rgba(255, 76, 81, 0.16)'
    },
    {
      label: __('Warning'),
      value: StatusLogsEnum.Warning,
      color: 'rgba(255, 159, 67, 1)',
      backgroundColor: 'rgba(255, 159, 67, 0.16)'
    },
    {
      label: __('Information'),
      value: StatusLogsEnum.Information,
      color: 'rgba(0, 186, 209, 1)',
      backgroundColor: 'rgba(0, 186, 209, 0.16)'
    },
    {
      label: __('Success'),
      value: StatusLogsEnum.Success,
      color: 'rgba(40, 199, 111, 1)',
      backgroundColor: 'rgba(40, 199, 111, 0.16)'
    }
  ]

  const handleRemoveType = (typeToRemove: string) => {
    const newSelectedTypes = selectedTypes.filter(type => type !== typeToRemove)

    setSelectedTypes(newSelectedTypes)
  }

  const paramsLogs = useMemo(() => {
    return {
      month: searchQuery.get('month'),
      object_type: 'user',
      object_id: window.location.pathname.split('/')[3],
      type: selectedTypes.length > 0 ? selectedTypes : Object.values(StatusLogsEnum),
      page: searchQuery.get('page'),
      perPage: searchQuery.get('limit'),
      keyword: searchQuery.get('search_user_logs')
    }
  }, [searchQuery, selectedTypes])

  const { data: logs, refetch } = useQuery({
    ...Activity.getActivityLogs(paramsLogs),
    enabled: true
  })

  const getAvatar = (params: Pick<IUser, 'avatar_file' | 'first_name'>) => {
    const { avatar_file, first_name } = params

    if (avatar_file) {
      return <CustomAvatar src={avatar_file.tiny_url} skin='light' size={34} />
    } else {
      return (
        <CustomAvatar skin='light' size={34}>
          {getInitials(first_name as string)}
        </CustomAvatar>
      )
    }
  }

  const getColor = (type: string) => {
    switch (type) {
      case StatusLogsEnum.Error:
        return {
          color: '#ff4c51',
          backgroundColor: '#ff4c5129'
        }
      case StatusLogsEnum.Warning:
        return {
          color: '#ff9f43',
          backgroundColor: '#ff9f4329'
        }
      case StatusLogsEnum.Information:
        return {
          color: '#00b4d8',
          backgroundColor: '#00b4d829'
        }
      case StatusLogsEnum.Success:
        return {
          color: 'rgba(40, 199, 111, 1)',
          backgroundColor: 'rgba(40, 199, 111, 0.16)'
        }
    }

    return {
      color: '#00b4d8',
      backgroundColor: '#00b4d829'
    }
  }

  const getIcon = (type: string) => {
    switch (type) {
      case StatusLogsEnum.Error:
        return <img src='/images/icons/icon-error.png' alt='' className='w-[30px] h-[30px]' />
      case StatusLogsEnum.Warning:
        return <img src='/images/icons/icon-warning.png' alt='' className='w-[30px] h-[30px]' />
      case StatusLogsEnum.Information:
        return <img src='/images/icons/icon-information.png' alt='' className='w-[30px] h-[30px]' />
      case StatusLogsEnum.Success:
        return <img src='/images/icons/icon-success.png' alt='' className='w-[30px] h-[30px]' />
    }
  }

  const handleRefresh = () => {
    setSelectedTypes(Object.values(StatusLogsEnum))
    updateQuery({ page: 1, search_user_logs: undefined })
    refetch()
  }

  return (
    <>
      <Box>
        <Box
          component={'div'}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 5
          }}
        >
          {<i className='tabler-chevron-left cursor-pointer' onClick={() => navigate(-1)}></i>}

          <Typography variant='h3'>{__('History Menu lift detail')}</Typography>

          <Divider orientation='vertical' flexItem />

          <Breadcrumbs aria-label='breadcrumb' separator={<i className='tabler-chevron-right' />}>
            <Link color='inherit' href='/cms'>
              <i className='tabler-home' />
            </Link>
            <Link color='inherit' href='/cms/users/lift-user'>
              {__('Lift Management')}
            </Link>
            <Typography sx={{ color: 'text.primary' }}>{__('History')}</Typography>
          </Breadcrumbs>
        </Box>
      </Box>

      <Card className='rounded-[32px]'>
        <CardContent>
          <FilterMonthAttendance
            onDateChange={month => {
              updateQuery({ month })
            }}
            status={''}
          />
        </CardContent>
      </Card>

      <Card className='rounded-[32px] mt-6'>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              placeholder={__('Search User')}
              value={searchQuery.get('search_user_logs') || ''}
              variant='standard'
              fullWidth
              onChange={e => updateQuery({ search_user_logs: e.target.value, page: 1 })}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position='start'>
                      <i className='tabler-search' style={{ fontSize: '24px' }} />
                    </InputAdornment>
                  )
                }
              }}
              sx={{
                '& .MuiInputBase-root': {
                  padding: '8px 0px'
                }
              }}
            />

            <Box className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <Typography variant='body2' sx={{ color: 'text.secondary', minWidth: 'fit-content' }}>
                  {__('Select Status')}:
                </Typography>
                <div className='flex items-center gap-2'>
                  {attendanceTypeOptions
                    .filter(option => selectedTypes.includes(option.value))
                    .map(option => (
                      <div
                        key={option.value}
                        className={`rounded-full px-4 py-2 flex items-center gap-2`}
                        style={{ backgroundColor: option.backgroundColor }}
                      >
                        <span className='font-medium' style={{ color: option.color }}>
                          {option.label}
                        </span>
                        <i
                          className='tabler-circle-x cursor-pointer'
                          style={{ fontSize: '16px', color: option.color }}
                          onClick={() => handleRemoveType(option.value)}
                        />
                      </div>
                    ))}
                </div>
              </div>
              <IconButton onClick={handleRefresh}>
                <i className='tabler-refresh' />
              </IconButton>
            </Box>
          </Box>
          <Divider className='my-3 border-b-[1px] border-[#E0E0E0]' />

          <div className='mt-6'>
            {logs?.data.map((log, index) => (
              <div
                key={index}
                className='flex items-center justify-between gap-2 rounded-[24px] p-4 mb-4 last:mb-0'
                style={{ backgroundColor: getColor(log.type).backgroundColor }}
              >
                <div className='flex items-center gap-2'>
                  {getIcon(log.type)}
                  <div>
                    <div className='text-[18px] font-medium capitalize' style={{ color: getColor(log.type).color }}>
                      {log.type || '-'}
                    </div>
                    <div className='text-[15px] font-normal' style={{ color: getColor(log.type).color }}>
                      {log.message || '-'}
                    </div>
                  </div>
                </div>
                <div className='flex items-center gap-2'>
                  <div className='text-[15px] font-normal'>{dayjs(log.created_at).format('DD/MM/YYYY HH:mm')}</div>
                  <div className='w-[10px] h-[10px] rounded-full bg-[#ff9f43]'></div>
                  <div className='flex items-center gap-2'>
                    <div className='text-[15px] font-medium'>{log?.causer?.first_name}</div>
                    <div>
                      {getAvatar({
                        avatar_file: log?.causer?.avatar_file,
                        first_name: log?.causer?.first_name || log?.causer?.employee_name || '-'
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <TablePagination
            meta={{
              total: logs?.meta?.total || 0,
              perPage: logs?.meta?.perPage || 10,
              page: Number(searchQuery.get('page')) || 1
            }}
          />
        </CardContent>
      </Card>
    </>
  )
}
